/**
 * Sync Health Dashboard
 *
 * Pregled zdravlja svih syncova na jednom mjestu:
 *  - kartica po shopu: status (ok/warn/dead), zadnji batch, ritam, 24h brojevi
 *  - zadnji Telegram alarmi (alert_log)
 *  - CDN pokrivenost slika
 *
 * Podaci: GET /api/admin/sync-health (agregira plugin_sync_batches, alert_log,
 * feed_import_logs, products). Auto-refresh svakih 60s.
 */

import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  Warning as WarnIcon,
  Error as DeadIcon,
  HelpOutline as NoneIcon,
} from '@mui/icons-material';
import { adminApi } from '../../lib/adminApi';

const HEALTH_META: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'error' | 'default'; icon: JSX.Element }
> = {
  ok: { label: 'OK', color: 'success', icon: <OkIcon fontSize="small" /> },
  warn: { label: 'Upozorenje', color: 'warning', icon: <WarnIcon fontSize="small" /> },
  dead: { label: 'Sync mrtav', color: 'error', icon: <DeadIcon fontSize="small" /> },
  none: { label: 'Nikad sinhao', color: 'default', icon: <NoneIcon fontSize="small" /> },
};

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return 'upravo';
  if (diffMin < 60) return `prije ${diffMin} min`;
  const h = Math.floor(diffMin / 60);
  if (h < 48) return `prije ${h}h ${diffMin % 60}min`;
  return `prije ${Math.floor(h / 24)} dana`;
}

export default function SyncHealth() {
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['sync-health'],
    queryFn: () => adminApi.syncHealth.get(),
    refetchInterval: 60_000,
  });

  if (isLoading) return <LinearProgress />;
  if (error) {
    return (
      <Typography color="error" sx={{ p: 3 }}>
        Greška pri učitavanju: {(error as Error).message}
      </Typography>
    );
  }

  const shops = (data?.shops || []).filter((s: any) => s.is_active);
  const inactive = (data?.shops || []).filter((s: any) => !s.is_active);
  const cdn = data?.cdn;
  const cdnPct = cdn?.with_feed_images
    ? Math.round((cdn.with_cdn / cdn.with_feed_images) * 100)
    : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2 }}>
        <Typography variant="h5">Sync Health</Typography>
        <Typography variant="body2" color="text.secondary">
          osvježeno {timeAgo(new Date(dataUpdatedAt).toISOString())} · auto-refresh 60s
        </Typography>
      </Box>

      {/* Kartice po shopu */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {shops.map((s: any) => {
          const meta = HEALTH_META[s.health] || HEALTH_META.none;
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={s.shop_id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap title={s.name}>
                      {s.name}
                    </Typography>
                    <Chip size="small" color={meta.color} icon={meta.icon} label={meta.label} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Zadnji sync: <b>{timeAgo(s.last_batch?.created_at)}</b>
                    {s.median_interval_hours != null && ` (ritam ~${s.median_interval_hours}h)`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    24h: {s.batches_24h} batcheva
                    {s.failed_24h > 0 && (
                      <Tooltip title="Proizvodi koji nisu prošli u zadnja 24h">
                        <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
                          {' '}· {s.failed_24h} failed
                        </Box>
                      </Tooltip>
                    )}
                    {s.unreported_24h > 0 && ` · ${s.unreported_24h} unreported`}
                  </Typography>
                  {s.last_batch && (
                    <Typography variant="caption" color="text.secondary">
                      zadnji batch: {s.last_batch.processed_items}/{s.last_batch.total_items} ({s.last_batch.status})
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* CDN + feed greške */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                CDN slike (cdn.sejkan.com)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={cdnPct}
                    sx={{ height: 10, borderRadius: 1 }}
                  />
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {cdnPct}%
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {cdn?.with_cdn?.toLocaleString()} obrađeno / {cdn?.with_feed_images?.toLocaleString()} sa
                slikama u feedu · backlog {cdn?.backlog?.toLocaleString()} · ukupno{' '}
                {cdn?.total_products?.toLocaleString()} proizvoda
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Feed importi (24h)
              </Typography>
              <Typography variant="h4" color={data?.feed_errors_24h ? 'error.main' : 'success.main'}>
                {data?.feed_errors_24h || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                grešaka u zadnja 24 sata
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Zadnji alarmi */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Zadnji alarmi (Telegram)
          </Typography>
          {(data?.alerts || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nema alarma — sve mirno. 🎉
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 160 }}>Kad</TableCell>
                  <TableCell>Poruka</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.alerts.map((a: any) => (
                  <TableRow key={a.alert_key} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{timeAgo(a.sent_at)}</TableCell>
                    <TableCell>{a.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Neaktivni shopovi — samo spomen */}
      {inactive.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Neaktivni shopovi: {inactive.map((s: any) => s.name).join(', ')}
        </Typography>
      )}
    </Box>
  );
}
